import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  model,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { ModalService } from '../../../../shared/utils/services/modal.service';
import { ModalComponent } from '../../../../shared/ui/modal/modal.component';
import {
  PaymentReason,
  Payment,
  PaymentStatus,
  PaymentType,
} from '../../../../shared/schemas/payment';
import { OrgService } from '../../../../shared/services/org.service';
import { PaymentAccount } from '../../../../shared/schemas/payment_account';
import { FormsModule } from '@angular/forms';
import { PaymentServices } from '../../../../shared/services/payment-services.service';
import { TranslocoModule } from '@jsverse/transloco';
import { Locale } from '../../../../shared/schemas/locale';
import { QrCodeComponent } from '../../ui/qr-code/qr-code.component';
import { PaymentService } from '../../../../shared/services/payment.service';
import { Org } from '../../../../shared/schemas/org';
import { UserService } from '../../../../shared/services/user.service';
import { OrgPaymentsService } from '../../../../shared/services/payments.service';

@Component({
  selector: 'qwiko-request-payment',
  imports: [
    CommonModule,
    ModalComponent,
    FormsModule,
    TranslocoModule,
    QrCodeComponent,
  ],
  templateUrl: './request-payment.component.html',
  styleUrl: './request-payment.component.scss',
})
export class RequestPaymentComponent implements OnDestroy {
  modalService = inject(ModalService);
  paymentService = inject(PaymentService);
  orgService = inject(OrgService);
  orgPaymentsService = inject(OrgPaymentsService);
  userService = inject(UserService);
  services = inject(PaymentServices);

  org = computed<Org | null>(() => {
    return this.orgService.org();
  });

  amountInputRef = viewChild<ElementRef<HTMLInputElement>>('amount_number');
  amountInputRefEffect = effect(() => {
    if (this.amountInputRef() && this.amountInputRef()!.nativeElement) {
      this.amountInputRef()!.nativeElement.focus();
    }
  });
  measureRef = viewChild<ElementRef<HTMLSpanElement>>('measure');

  orgPaymentAccounts = this.orgPaymentsService.orgPaymentAccounts;
  country = computed<Locale>(() => {
    return this.orgService.org()?.country as Locale;
  });

  loading = model<boolean>(false);

  isRequestPayment = this.services.isRequestPayment;

  servicesEffect = effect(() => {
    if (this.isRequestPayment()) {
      this.modalService.open('request-payment');
      this.setDefaultPaymentAccount();
    }
  });

  // isCreatedPaymentEffect = effect(() => { console.log('memory leak')
  //   console.log('this.services.isRequestPayment():', this.services.isRequestPayment());
  // });

  isAddAmount = signal<boolean>(false);
  isAddCustomer = signal<boolean>(false);
  isOffLineMode = signal<boolean>(false);
  isChoosePaymentAccount = signal<boolean>(false);
  isAddPaymentReason = signal<boolean>(false);
  isQRCode = signal<boolean>(false);
  isShowQRCode = signal<boolean>(false);
  isConfirmPayment = signal<boolean>(false);
  isChangeAccountOnConfirm = signal<boolean>(false);
  hasNotConfirmed = signal<boolean>(false);
  isDoNotAsk = signal<boolean>(false);

  payment = signal<Payment | null>(null);

  amount = signal<string | null>('0');
  // amountFormatted = computed<string>(() => {
  //   const amount = parseInt(this.amount() ?? '0');
  //   if (isNaN(amount)) {
  //     return '0';
  //   }
  //   return amount.toLocaleString('en-US');
  // });
  amountToSave = computed<number | null>(() => {
    if (this.amount() == null) {
      return 0;
    }
    const amount = this.amount()!.split(',').join('');
    return parseInt(amount);
  });

  customerNumber = signal<string | null>(null);
  customerNumberFormatted = computed<string>(() => {
    const code = this.country()?.phone_code;
    if (!this.customerNumber()) {
      return '';
    }
    return `${code}${this.customerNumber()}`;
  });

  customerNumberDisplay = computed<string>(() => {
    const code = this.country()?.phone_code;
    const starts_with = this.country()?.starts_with;

    if (this.customerNumberFormatted().startsWith(code!) && starts_with) {
      const index = this.customerNumberFormatted().indexOf(starts_with!);

      if (index !== -1) {
        const sliceIndex = index + starts_with!.length;
        const remainder = this.customerNumberFormatted().slice(sliceIndex);

        return `${starts_with}${remainder}`;
      }
    }

    return this.customerNumberFormatted();
  });

  status = signal<PaymentStatus>(PaymentStatus.INITIATED);
  defaultPaymentAccount = signal<PaymentAccount | null>(null);
  // defaultPaymentAccountEffect = effect(() => { console.log('memory leak')
  //   if (this.defaultPaymentAccount()) {
  //     // this.paymentAccount.set(this.defaultPaymentAccount());
  //     console.log('defaultPaymentAccountEffect');
  //   }
  // });
  defaultPaymentAccountId = computed(() => {
    return this.defaultPaymentAccount()?.id
      ? this.defaultPaymentAccount()?.id
      : null;
  });
  isDefaultAccount = computed(() => {
    if (
      this.paymentAccount() === null ||
      this.defaultPaymentAccountId() === null
    )
      return false;
    return this.paymentAccount()?.id === this.getDefaultAccountId()
      ? true
      : false;
  });
  paymentAccount = signal<PaymentAccount | null>(null);
  paymentReason = signal<PaymentReason | null>(null);

  addAmount() {
    this.resetModalStates();
    this.isAddAmount.set(true);
  }

  choosePaymentAccount() {
    this.resetModalStates();
    this.isChoosePaymentAccount.set(true);
  }

  changePaymentAccount(event: boolean = false) {
    if (this.isConfirmPayment()) {
      this.isChangeAccountOnConfirm.set(true);
    }
    this.resetModalStates();
    this.isShowQRCode.set(event);
    this.isChoosePaymentAccount.set(true);
    this.hasNotConfirmed.set(true);
  }

  toggleDefaultAccount(event: any) {
    const paymentAccountId = this.paymentAccount()?.id;
    if (paymentAccountId) {
      if (event) {
        localStorage.setItem('defaultPaymentAccountId', paymentAccountId);
        const account = this.getDefaultPaymentAccount();
        this.defaultPaymentAccount.set(account);
      } else {
        localStorage.removeItem('defaultPaymentAccountId');
        this.defaultPaymentAccount.set(null);
      }
    }
  }

  getDefaultAccountId() {
    return localStorage.getItem('defaultPaymentAccountId');
  }

  setDefaultPaymentAccount() {
    if (this.paymentAccount() === null) {
      const account = this.getDefaultPaymentAccount();
      this.paymentAccount.set(account);
      this.defaultPaymentAccount.set(account);
    }
  }

  getDefaultPaymentAccount() {
    const paymentAccount = this.orgPaymentAccounts().find(
      (account) => account.id === this.getDefaultAccountId(),
    );
    return paymentAccount ? paymentAccount : null;
  }

  async paymentConfirmation(event?: boolean) {
    if (this.isShowQRCode()) {
      this.isShowQRCode.set(false);
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    // if (event && this.hasNotConfirmed()) {
    //   await new Promise((resolve) => setTimeout(resolve, 2000));
    // }
    this.resetModalStates();
    this.isConfirmPayment.set(true);
    this.isChangeAccountOnConfirm.set(false);
    this.hasNotConfirmed.set(true);
    this.loading.set(false);
  }

  cancelPayment() {
    this.resetModalStates();
    this.status.set(PaymentStatus.CANCELED);
    this.updatePayment();
  }

  async savePayment(event?: boolean) {
    this.loading.set(true);
    const payment: Payment = {
      // id: crypto.randomUUID(),
      type: PaymentType.PAYMENT,
      status: PaymentStatus.PENDING,
      amount: this.amountToSave()!.toString(),
      customer_number: this.customerNumberFormatted() || null,
      receiver_status: PaymentStatus.PENDING,
      // receiver: this.userService.user()?.id || null,
      receiver_account:
        this.paymentAccount()?.id || this.defaultPaymentAccount()?.id || null,
      org_receiver: this.org()?.id,
      country: this.country()?.id,
      was_offline: this.isOffLineMode(),
      // date_created: new Date().toISOString(),
    };

    if (!this.hasNotConfirmed()) {
      // console.log('payment', payment);
      const paymentResponse = await this.paymentService.createPayment(payment);
      this.payment.set(paymentResponse);
    }

    this.loading.set(false);

    // if (event && this.hasNotConfirmed()) {
    //   await new Promise((resolve) => setTimeout(resolve, 3000));
    // }
    // this.paymentConfirmation();
  }

  async confirmPayment() {
    this.status.set(PaymentStatus.CONFIRMED);
    await this.updatePayment();
  }

  async updatePayment(event?: boolean) {
    this.loading.set(true);

    const payment: Payment = {
      id: this.payment()?.id,
      status:
        this.status() === PaymentStatus.INITIATED
          ? this.payment()?.status
          : this.status(),
      // amount: this.amountToSave()!.toString(),
      // customer_number: this.customerNumberFormatted() || null,
      receiver_account: this.paymentAccount()?.id || null,
      receiver_status: event ? PaymentStatus.PENDING : this.status(),
    };
    await this.paymentService.updatePayment(payment);
    if (event) {
      this.paymentConfirmation(event);
      return;
    }

    this.loading.set(false);
    this.resetAllStates();
    this.isRequestPayment.set(true);
  }

  async selectPaymentAccount(account: PaymentAccount | null) {
    this.paymentAccount.set(account);
    this.resetModalStates();
    if (this.isOffLineMode()) {
      if (this.hasNotConfirmed() && !this.isShowQRCode()) {
        this.isConfirmPayment.set(true);
        this.isChangeAccountOnConfirm.set(false);
        this.hasNotConfirmed.set(false);
        return;
      }

      if (this.isShowQRCode()) {
        this.isQRCode.set(true);
        this.isShowQRCode.set(false);
        return;
      }

      this.isQRCode.set(true);
      return;
    }

    if (this.hasNotConfirmed() && !this.isShowQRCode()) {
      this.isConfirmPayment.set(true);
      this.isChangeAccountOnConfirm.set(false);
      this.hasNotConfirmed.set(false);
      // await this.updatePayment(true);
      return;
    }

    if (this.isShowQRCode()) {
      this.isQRCode.set(true);
      this.isShowQRCode.set(false);
      // await this.updatePayment(true);
      return;
    }

    this.isRequestPayment.set(true);
  }

  // allAccounts() {
  //   this.paymentAccount.set(null);
  //   this.resetModalStates();
  //   if (this.isOffLineMode()) {
  //     if (this.hasNotConfirmed()) {
  //       this.isConfirmPayment.set(true);
  //       this.isChangeAccountOnConfirm.set(false);
  //       this.hasNotConfirmed.set(false);
  //       return;
  //     }
  //     this.isOffLineMode.set(false);
  //     this.isQRCode.set(true);
  //     return;
  //   }

  //   if (this.hasNotConfirmed()) {
  //     this.isConfirmPayment.set(true);
  //     this.isChangeAccountOnConfirm.set(false);
  //     this.hasNotConfirmed.set(false);
  //     return;
  //   }
  //   this.isRequestPayment.set(true);
  // }

  addPaymentReason() {
    this.resetModalStates();
    this.isAddPaymentReason.set(true);
  }

  selectPaymentReason(reason: PaymentReason) {
    this.paymentReason.set(reason);
    this.resetModalStates();
    this.isRequestPayment.set(true);
  }

  async openQRCode() {
    await this.savePayment();
    this.resetModalStates();
    this.isQRCode.set(true);
  }

  createPayment() {
    this.closeModal();
  }

  customerNumberChange(event?: any) {
    if (this.customerNumber() === null || this.customerNumber() === '0') {
      return;
    }

    // const starts_with = this.country()?.starts_with;
    let number: string = this.customerNumber()?.split(' ').join('') || '';
    number = number.replace(/[^0-9]/g, '');
    const intNumber: number = parseInt(number);
    const numberString: string = intNumber.toString();

    if (isNaN(intNumber) || intNumber == 0) {
      this.customerNumber.set(null);
      return;
    }
    this.customerNumber.set(numberString);
    return;
  }

  formatCustomerNumberOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.customerNumber.set(pastedText ?? '');
    this.customerNumberChange();
  }

  formatAmount(event?: any) {
    // console.log('event:', event);
    // console.log('amount:', this.amount());
    if (event && event.key == 'Enter') {
      this.back();
    }

    if (this.amount() === null || this.amount() === '0') {
      return;
    }

    let number: string | number = this.amount()!;

    number = number.replace(/[\s,]/g, '');
    number = number.replace(/[^0-9,]/g, '');

    let numberInt = parseInt(number);

    // console.log('number:', number);
    // console.log('numberInt', numberInt);

    if (isNaN(numberInt) || numberInt == 0) {
      // console.log('is 0 or NaN', numberInt);
      this.amount.set(null);
      // console.log('amount after:', this.amount());
      return;
    }

    number = numberInt.toLocaleString('en-US');
    this.amount.set(number);

    return;
  }

  formatOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.amount.set(pastedText ?? '');
    this.formatAmount();
  }

  // transform(event: any) {
  //   if (this.measureRef() && this.amountInputRef()) {
  //     // Add extra padding to prevent text from touching the edges
  //     const width = Math.min(
  //       this.measureRef()!.nativeElement.offsetWidth + 10,
  //       this.amountInputRef()!.nativeElement.parentElement?.offsetWidth ||
  //         Infinity,
  //     );
  //     this.amountInputRef()!.nativeElement.style.width = `${width}px`;
  //   }
  // }

  closeModal() {
    this.resetAllStates();
    this.modalService.closeAll();
  }

  back(modal?: string) {
    if (this.isQRCode() && this.hasNotConfirmed()) {
      this.resetAllStates();
    }

    if (this.isChangeAccountOnConfirm() && this.isChoosePaymentAccount()) {
      this.resetModalStates();
      this.isConfirmPayment.set(true);
      this.isChangeAccountOnConfirm.set(false);
      return;
    }

    if (this.isConfirmPayment()) {
      this.resetModalStates();
      this.isQRCode.set(true);
      return;
    }

    if (this.isShowQRCode()) {
      this.resetModalStates();
      this.isQRCode.set(true);
      this.isShowQRCode.set(false);
      return;
    }

    this.resetModalStates();
    // this.services.isRequestPayment.set(false);
    this.isRequestPayment.set(true);
    // if(this.isOffLineMode()) {
    //   this.isOffLineMode.set(false)
    // }
  }

  resetAllStates() {
    this.resetModalStates();
    this.amount.set('0');
    this.paymentAccount.set(null);
    this.defaultPaymentAccount.set(null);
    this.paymentReason.set(null);
    this.customerNumber.set('');
    this.isOffLineMode.set(false);
    this.hasNotConfirmed.set(false);
    this.isShowQRCode.set(false);
    this.isChangeAccountOnConfirm.set(false);
    this.payment.set(null);
    this.status.set(PaymentStatus.INITIATED);
  }

  resetModalStates() {
    this.isAddAmount.set(false);
    this.isChoosePaymentAccount.set(false);
    this.isConfirmPayment.set(false);
    this.isAddPaymentReason.set(false);
    this.isQRCode.set(false);
    // this.services.isRequestPayment.set(false);
    this.isRequestPayment.set(false);
  }

  ngOnDestroy(): void {
    this.resetAllStates();
  }
}
